import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import _, { noop, isFunction } from 'underscore';
import {
  AssetEventFeature,
  getFormattedThemeForApplying,
  getUniqueAssetsFromThemes,
  useEventLog,
} from '@bingads-webui-campaign-react/copilot-common';
import { uniqAssetRecommendationsInThemes, useRecommendations, RecommendationContext } from '@bingads-webui-campaign-react/perf-max-recommendation';
import { constants as ImageassetsConstants } from '@bingads-webui-campaign-react/image-experience-model';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { CopilotRecommendationBannerWithDialog } from '@bingads-webui/inline-copilot';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { usePostAssetEvent, AssetChannel, AssetScenario } from '@bingads-webui-react/copilot-event-provider';
import { isCopilotRegionRestricted } from '@bingads-webui-react/copilot-utils';

import { usePrefill } from './use-prefill';
import { useSurveyTriggers } from './use-survey-triggers';
import { getUniqueRecommendationIdsFromThemes } from '../../copilot-common/src/get-unique-assets-from-themes';

export const SuggestionViewImageCount = 5;

export const useCopilot = ({
  maxUploadNum,
  addNewShortHeadlinesCallback,
  addNewLongHeadlinesCallback,
  addNewDescriptionCallback,
  imageExperienceAddNewImageCallback,
  newCTACallback,
  currentCTA,
  onReset,
  cid,
  aid,
  serverContext,
  instrumentation,
  data,
  isSaving,
  isCanceling,
  campaignId,
  assetGroupId,
  enableCopilotRecommendations = true,
  feature = 'DisplayPlus',
  assetScenario,
  customBannerStrings,
  toastOffset,
  isBannerNarrow,
  skipBusinessNameRecommendation = false,
}) => {
  const postAssetEventScenario = useMemo(() => {
    if (assetScenario) {
      return assetScenario;
    }
    return data !== undefined ? AssetScenario.EDIT_WORK_FLOW : AssetScenario.CREATE_WORK_FLOW;
  }, [assetScenario, data]);
  const [recommendationContext, setRecommendationContext] = useContext(RecommendationContext);
  const activity = useContext(ActivityContext);

  const { Permissions, DynamicPermissions } = serverContext || { };
  const permissions = useMemo(() => ({ dynamic: { ...DynamicPermissions }, ...Permissions }), [DynamicPermissions, Permissions]);

  const [isRegionRestricted, setIsRegionRestricted] = useState(true);
  useEffect(() => {
    isCopilotRegionRestricted({
      feature: 'Recommendations',
      permissions,
      serverContext,
    }).then(setIsRegionRestricted);
  }, [permissions, serverContext]);

  const {
    copilotTracking,
    cta,
    isCustomerIdInExclusionList,
    logo,
    themes,
  } = recommendationContext;

  const finalUrlRecommendations = recommendationContext.finalUrls;

  const [applyRecommendationsResult, setApplyRecommendationsResult] = useState();

  const logTrace = useMemo(() => {
    if (instrumentation && instrumentation.AdsCopilot) {
      const scenario = instrumentation.AdsCopilot.create();
      return scenario.createActivity('AssetEvent').trace;
    }
    return { trace: noop, error: noop };
  }, [instrumentation]);


  const postAssetEventLog = usePostAssetEvent({
    shouldLog: isFunction(logTrace) && !isRegionRestricted,
    serverContext,
    channel: AssetChannel.WEB_UI,
    feature,
    scenario: postAssetEventScenario,
    logTrace,
    campaignId,
    assetGroupId,
    // Due to CORS issue in PmaxLite, we need to set withCredentials to false
    withCredentials: feature !== AssetEventFeature.PMAX_LITE,
  });

  // only prefill for new ads
  const [shouldPrefill, setShouldPrefill] = useState(!_.has(data, 'Id'));
  const FinalUrlForRecommendation = data && data.FinalUrlForRecommendation;

  const hasAssets = useMemo(() => {
    const images = data && data.Images;
    const headlines = _.compact(_.values(data && data.Headlines))|| [];
    const longHeadlines = _.compact(_.values((data && data.LongHeadlines))) || [];
    const descriptions = _.compact(_.values((data && data.Descriptions))) || [];
    if (images && images.ImageList && images.ImageList.length > 0) {
      return true;
    }

    if (headlines.length > 0 || longHeadlines.length > 0 || descriptions.length > 0) {
      return true;
    }

    return false;
  }, [data]);

  const eventLog = useEventLog(postAssetEventLog);
  const {
    logEnterFlow,
    logFinalUrlRecommendations,
    logFetchAssetsSucceeded,
    logApplyThemes,
    logSuggestionsViewed,
    logPrefillAssets,
    logRemoveAssets,
  } = eventLog;

  const {
    suggestionPoller,
    handleCopilotBannerFetch,
    handleCopilotBannerFetchSucceeded,
    handleCopilotBannerFetchFailed,
  } = useRecommendations({
    cid,
    aid,
    campaignId: campaignId || -1,
    permissions,
    finalUrl: FinalUrlForRecommendation,
    isDisplayPlus: feature === AssetEventFeature.DISPLAY_PLUS,
    enableCopilotRecommendations,
    instrumentation,
    isMMA: feature === AssetEventFeature.MMA,
    isSignup: feature === AssetEventFeature.PMAX_LITE,
    skipBusinessNameRecommendation,
  });

  const handleReset = useCallback(() => {
    onReset();
    copilotTracking.removeAllInitialSuggestions();
    logRemoveAssets();
  }, [logRemoveAssets, onReset, copilotTracking]);

  const handleFetchAssetsSucceeded = useCallback((finalUrl, assetsRecommendationData) => {
    logFetchAssetsSucceeded(finalUrl, assetsRecommendationData);
    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets(assetsRecommendationData.RecommendedAssets);
    }
  }, [logFetchAssetsSucceeded, copilotTracking]);

  const applyTextAssets = useCallback((formattedThemesAndAssets, field, maxSize) => {
    let applyFn;
    switch (field) {
      case 'longHeadlines': applyFn = addNewLongHeadlinesCallback; break;
      case 'descriptions': applyFn = addNewDescriptionCallback; break;
      default: applyFn = addNewShortHeadlinesCallback; break;
    }

    if (applyFn) {
      const newTextAssets = getUniqueAssetsFromThemes(formattedThemesAndAssets, field, maxSize);
      applyFn(newTextAssets);

      // log selected suggestions as viewed
      const newTextAssetRecommendationIds = getUniqueRecommendationIdsFromThemes(formattedThemesAndAssets, field, maxSize);

      if (copilotTracking) {
        const viewedTextSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(newTextAssetRecommendationIds);
        if (viewedTextSuggestionIds.length > 0) {
          logSuggestionsViewed(viewedTextSuggestionIds);
          copilotTracking.trackViewedSuggestionIds(viewedTextSuggestionIds);
        }
      }

      return newTextAssets;
    }
    return null;
  }, [addNewShortHeadlinesCallback, addNewLongHeadlinesCallback, addNewDescriptionCallback, copilotTracking, logSuggestionsViewed]);

  const onApplyCopilotRecommendation = useCallback((selectedThemes, selectedThemesAndAssets) => {
    logApplyThemes(selectedThemes);

    const formattedThemesAndAssets = _.map(selectedThemesAndAssets, getFormattedThemeForApplying);

    setApplyRecommendationsResult(undefined);
    setRecommendationContext({
      themes: [...formattedThemesAndAssets],
      ...uniqAssetRecommendationsInThemes(formattedThemesAndAssets),
    });

    applyTextAssets(formattedThemesAndAssets, 'headlines');
    applyTextAssets(formattedThemesAndAssets, 'longHeadlines');
    applyTextAssets(formattedThemesAndAssets, 'descriptions');

    const logCopilotViewedImageSuggestions = (suggestionIds) => {
      if (copilotTracking) {
        const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(suggestionIds);
        if (viewedImageSuggestionIds.length > 0) {
          logSuggestionsViewed([], viewedImageSuggestionIds);
          copilotTracking.trackViewedSuggestionIds(viewedImageSuggestionIds);
        }
      }
    };

    const availableNewImages = getUniqueAssetsFromThemes(formattedThemesAndAssets, 'images', maxUploadNum);
    if (imageExperienceAddNewImageCallback && availableNewImages && availableNewImages.length > 0) {
      imageExperienceAddNewImageCallback(availableNewImages, ImageassetsConstants.imageOptionKeys.Image)
        .then((response) => {
          if (response.processedImages && enableCopilotRecommendations) {
            logCopilotViewedImageSuggestions(_.pluck(response.processedImages, 'suggestionId'));
            response.processedImages.forEach((image) => {
              if (image) {
                copilotTracking.trackSelectedImageAssets({ ImageAssets: [{ suggestionId: image.suggestionId, imageId: image.idString }] });
              }
            });
          }
          if (response.errorMessage && activity) {
            const message = `Apply image from copilot dialog error: ${response.errorMessage}`;
            activity.trace(message);
          }
        }).then(() => {
          if (cta && newCTACallback && currentCTA !== 'Automated') {
            newCTACallback({
              CallToAction: cta.replace(' ', ''),
              CallToActionLanguage: 'English',
            });
          }
          if (logo) {
            return imageExperienceAddNewImageCallback([{
              fullUrl: logo,
              suggestionId: -1,
            }], ImageassetsConstants.imageOptionKeys.Logo);
          }
          return Promise.resolve();
        })
        .finally(() => {
          setApplyRecommendationsResult('ApplySucceeded');
        });
    } else {
      setApplyRecommendationsResult('ApplySucceeded');
    }

    // resolve immediately, recommendations dialog will be closed and toast will be shown while apply operations continue
    return Promise.resolve();
  }, [logApplyThemes, setRecommendationContext, applyTextAssets, imageExperienceAddNewImageCallback, maxUploadNum, copilotTracking, logSuggestionsViewed, enableCopilotRecommendations, activity, cta, newCTACallback, currentCTA, logo]);

  const copilotBanner = useMemo(() => {
    if (isRegionRestricted) {
      // copilot is not enabled in this region, do not display the banner or fetch any recommendations
      return null;
    }

    return (
      <FluentProvider theme={webLightTheme}>
        <CopilotRecommendationBannerWithDialog
          applyRecommendationsResult={applyRecommendationsResult}
          eventLog={eventLog}
          onDialogFetchAssetsSucceeded={handleFetchAssetsSucceeded}
          onApply={onApplyCopilotRecommendation}
          onFetch={handleCopilotBannerFetch}
          onFetchFailed={handleCopilotBannerFetchFailed}
          onFetchSucceeded={handleCopilotBannerFetchSucceeded}
          onReset={handleReset}
          customStrings={customBannerStrings}
          toastOffset={toastOffset}
          narrow={isBannerNarrow}
          isNewLogo={_.get(permissions, ['dynamic', 'AdsCopilotUxImprovements'])}
        />
      </FluentProvider>
    );
  }, [
    applyRecommendationsResult,
    customBannerStrings,
    eventLog,
    handleCopilotBannerFetch,
    handleCopilotBannerFetchFailed,
    handleCopilotBannerFetchSucceeded,
    handleFetchAssetsSucceeded,
    handleReset,
    isBannerNarrow,
    isRegionRestricted,
    onApplyCopilotRecommendation,
    toastOffset,
  ]);

  useEffect(() => {
    if (enableCopilotRecommendations) {
      logEnterFlow();

      setRecommendationContext({
        postAssetEventLog,
      });
    }
  }, [logEnterFlow, setRecommendationContext, postAssetEventLog, enableCopilotRecommendations]);

  useEffect(() => {
    if (enableCopilotRecommendations && finalUrlRecommendations && finalUrlRecommendations.length > 0) {
      logFinalUrlRecommendations(finalUrlRecommendations);
      copilotTracking.trackReturnedFinalUrlAssets({ FinalUrls: finalUrlRecommendations });
    }
  }, [finalUrlRecommendations, logFinalUrlRecommendations, copilotTracking, enableCopilotRecommendations]);

  const logAndTrackPrefillImages = useCallback((imageAssets) => {
    if (copilotTracking && imageAssets && imageAssets.length > 0) {
      copilotTracking.trackSelectedImageAssets({ ImageAssets: imageAssets });
      const viewedImageSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(_.pluck(imageAssets, 'suggestionId'));
      if (viewedImageSuggestionIds.length > 0) {
        logSuggestionsViewed([], viewedImageSuggestionIds);
      }
    }
  }, [copilotTracking, logSuggestionsViewed]);

  const prefill = usePrefill({
    activity,
    applyTextAssets,
    imageExperienceAddNewImageCallback,
    logAndTrackPrefillImages,
    copilotTracking,
    cta,
    newCTACallback,
    currentCTA,
    logo,
    feature,
    permissions,
  });
  useEffect(() => {
    if (enableCopilotRecommendations && shouldPrefill) {
      if (_.size(themes) === 0) {
        activity.trace('not prefilling ad, no copilot recommendations');
      } else if (hasAssets) {
        activity.trace('not prefilling ad, text or image assets already exist');
      } else {
        // prevent prefilling again
        setShouldPrefill(false);

        if (isCustomerIdInExclusionList) {
          activity.trace('not prefilling ad, customer is in exclusion list');
        } else {
          // new asset group or campaign with no text or image assets, prefill with copilot recommendations
          activity.trace('prefilling ad with copilot recommendations');
          setApplyRecommendationsResult(undefined);
          prefill(themes).finally(() => {
            setApplyRecommendationsResult('ApplySucceeded');
            logPrefillAssets();
          });
        }
      }
    }
  }, [
    activity,
    enableCopilotRecommendations,
    hasAssets,
    isCustomerIdInExclusionList,
    logPrefillAssets,
    prefill,
    shouldPrefill,
    themes,
  ]);


  useSurveyTriggers({
    enableCopilotRecommendations,
    isSaving,
    isCanceling,
    themes,
  });

  return {
    copilotBanner,
    suggestionPoller,
    themes,
  };
};
